import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { useLogout } from "@/hooks/useAuth";
import DrawerButton from "@assets/tsxIcons/DrawerButton";

import {
  QUERY_KEYS,
  getStructuredSections,
  comingSoonRoutes,
} from "@/constants";
import type { SidebarSection, AppRoute, SettingsRoute } from "@/constants";

import logoutLogo from "@assets/svgs/icon_logout_new.svg";
import logo from "@assets/svgs/Logo white.svg";
import logoIcon from "@assets/svgs/logo.svg";
import setting from "@assets/svgs/icon_settings_new.svg";
import help from "@assets/svgs/icon_help_new.svg";

// Import new sidebar icons that match the Figma design
import dashboardIcon from "@assets/svgs/icon_dashboard_new.svg";
import bayIcon from "@assets/svgs/icon_bookings_new.svg"; // Simulator bookings
import lessonIcon from "@assets/svgs/icon_lessons_new.svg"; // Person with circular arrow
import teeTimeIcon from "@assets/svgs/icon_tee_times_new.svg"; // Clock icon
import golfCourseIcon from "@assets/svgs/icon_course_management_new.svg"; // Golf flag
import cartIcon from "@assets/svgs/icon_cart_fleet_new.svg"; // Golf cart
import caddieIcon from "@assets/svgs/icon_caddies_new.svg"; // Golf bag
import staffIcon from "@assets/svgs/icon_staff_scheduler_new.svg"; // Person with clock
import inboxIcon from "@assets/svgs/icon_inbox_new.svg"; // Envelope
import eventsIcon from "@assets/svgs/icon_events_new.svg"; // Calendar with star
import customersIcon from "@assets/svgs/icon_customers_new.svg"; // Single person
import membershipsIcon from "@assets/svgs/memberships.svg"; // ID card
import transactionsIcon from "@assets/svgs/icon_transactions_new.svg"; // Credit card
import analyticsIcon from "@assets/svgs/icon_analytics_new.svg"; // Line chart
import websiteIcon from "@assets/svgs/icon_website_cms_new.svg"; // Monitor/browser
import usersPermissionsIcon from "@assets/svgs/icon_users_permissions_new.svg"; // Two people
import leagueManagerNavIcon from "@assets/svgs/leagueManager1.svg";
import { clearSentryUser } from "@utils/sentry";
import useGetData from "@/services/useGetData";
import { API_URLS } from "@/api/urls";
import { useFeaturesPopupStore } from "@/zustand/useFeaturesPopupStore";
import DownArrow from "@assets/tsxIcons/DownArrow";
import { useClickAway } from "react-use";
import { useCan } from "@/contexts/PermissionContext";
import useConfirmNavigation from "@/hooks/useConfirmNavigation";
import LockIcon from "@assets/tsxIcons/LockIcon";
import useSidebarStore from "@/zustand/useSidebarStore";
import { useVenueConfigurationStore } from "@/zustand/useVenueConfigurationStore";
import useFacilityStore from "@/zustand/useFacilityStore";
import useTenantStore from "@/zustand/useTenantStore";
import { selectTenant } from "@/services/centralAuthService";
import SettingsSidebarPanel from "@shared/SettingsSidebarPanel";
import LeagueSidebarPanel from "@shared/LeagueSidebarPanel";
import { useTeeAgentStore } from "@/zustand/useTeeAgentStore";
import { AgentOrb } from "@/components/TeeAgent/AgentOrb";

const AppSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has, hasAny } = useCan();

  // Facility context for dynamic routes
  const { facilityType: _facilityType } = useFacilityStore();

  const { setOpenReleasingPopup } = useFeaturesPopupStore();
  const {
    guardedNavigate,
    hasUnsavedChanges,
    setShowUnsavedChangesNotification,
  } = useConfirmNavigation();

  // Use Zustand store for sidebar collapse state
  const {
    isCollapsed: showNav,
    setIsCollapsed: setShowNav,
    toggleCollapsed: toggleShowNav,
    isSettingsPanelOpen,
    setSettingsPanelOpen,
    toggleSettingsPanel,
    isLeaguePanelOpen,
    setLeaguePanelOpen,
  } = useSidebarStore();

  const [showVenuDropDown, setShowVenuDropDown] = useState<boolean>(false);
  const [showComingDropDown, setShowComingDropDown] = useState<boolean>(false);
  const [expandedSections, _setExpandedSections] = useState<
    Record<string, boolean>
  >({
    general: true,
    golf: true,
    bays: true,
  });
  const [expandedSettingsMenus, setExpandedSettingsMenus] = useState<
    Record<string, boolean>
  >({
    golf: false,
    bays: false,
  });

  const venuDropDownRef = useRef<HTMLDivElement>(null);
  const comingDropDownRef = useRef<HTMLDivElement>(null);

  useClickAway(venuDropDownRef, () => {
    if (showVenuDropDown) {
      setShowVenuDropDown(false);
    }
  });
  useClickAway(comingDropDownRef, () => {
    if (showComingDropDown) {
      setShowComingDropDown(false);
    }
  });

  const [showFullNav, setShowFullNav] = useState<boolean>(false);
  const [hoveredItemTitle, setHoveredItemTitle] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const { mutate: logout, isPending } = useLogout();
  const {
    agentState,
    toggleOpen: toggleTeeAgent,
    isOpen: isTeeAgentOpen,
  } = useTeeAgentStore();
  const { data: statsData } = useGetData<IsubscriptionStats>(
    [QUERY_KEYS.TOTAL_NEW_SUBMISSIONS],
    `${API_URLS.SUBMISSIONS.GET_SUBSCRIPTION_STATS}?type=total_new_submissions`,
    { refetchOnWindowFocus: true, staleTime: 0 },
  );

  // Fetch venue settings for dynamic venue name and section visibility
  // Type allows for both array (expected from ListAPIView) and single object (fallback)
  type VenueSettingsItem = {
    name: string;
    id?: number;
    city?: string;
    has_golf_course?: boolean;
    has_bays?: boolean;
    has_events?: boolean;
    has_lessons?: boolean;
    has_memberships?: boolean;
    has_website_builder?: boolean;
  };
  const { data: venueSettings } = useGetData<
    VenueSettingsItem[] | VenueSettingsItem
  >(["venue-settings"], API_URLS.VENUE.GET_VENUE_SETTINGS, {
    refetchOnWindowFocus: false,
  });

  // Determine which sections to show based on venue settings and local configuration
  // Handle both array response (from ListAPIView) and potential single object response
  const venueSettingsData: VenueSettingsItem | undefined = Array.isArray(
    venueSettings,
  )
    ? venueSettings[0]
    : venueSettings;

  // Use local configuration store
  const { isGolfEnabled, isBaysEnabled } = useVenueConfigurationStore();

  // The tenant's enabled services (seeded into VenueSetting at deploy time) are
  // the authoritative source for sidebar visibility. The local Venue Config
  // toggle is ANDed in for golf/bays so it can only further restrict — never
  // re-enable a service the admin didn't select.
  // Default fail-closed: when the flag is absent (settings not yet loaded or an
  // older tenant DB missing the column), hide the section rather than showing a
  // service the admin never enabled.
  const hasGolfCourse =
    (venueSettingsData?.has_golf_course ?? false) && isGolfEnabled;
  const hasBays = (venueSettingsData?.has_bays ?? false) && isBaysEnabled;

  // Service-specific items have no local toggle; hide unless explicitly enabled.
  const hasEvents = venueSettingsData?.has_events ?? false;
  const hasLessons = venueSettingsData?.has_lessons ?? false;
  const hasMemberships = venueSettingsData?.has_memberships ?? false;
  const hasWebsiteBuilder = venueSettingsData?.has_website_builder ?? false;

  // Get structured sections based on venue configuration
  const sections = getStructuredSections({
    hasGolfCourse,
    hasBays,
    hasEvents,
    hasLessons,
    hasMemberships,
    hasWebsiteBuilder,
  });

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const isXlScreen = window.innerWidth >= 1440;
      // On mobile/tablet (< xl), always collapse
      if (!isXlScreen) {
        setShowNav(true);
        setShowVenuDropDown(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavigate = (path: string) => {
    guardedNavigate(path);
  };

  const handleLogout = () => {
    if (hasUnsavedChanges) {
      handleNavigate("/login");
      setShowUnsavedChangesNotification(true);
      return;
    }

    const refreshToken = localStorage.getItem("refreshToken");

    if (refreshToken) {
      logout(
        { refresh: refreshToken },
        {
          onSuccess: (response) => {
            if (response) {
              // Clear all React Query cache to ensure fresh data for next user
              queryClient.clear();
              queryClient.removeQueries();

              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              clearSentryUser();
              navigate("/login");
            }
          },
          onError: (error) => {
            console.error("Logout Error:", error);
          },
        },
      );
    }
  };

  const toggleNav = () => {
    // Only allow toggle on xl screens and above
    if (window.innerWidth >= 1440) {
      toggleShowNav();
      setShowVenuDropDown(false);
    }
  };

  const toggleSettingsMenu = (sectionId: string) => {
    setExpandedSettingsMenus((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleMouseEnter = (
    title: string,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    // Only show tooltips when collapsed and on xl screens
    if (showNav && window.innerWidth >= 1440) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 12,
      });
      setHoveredItemTitle(title);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItemTitle(null);
    setTooltipPosition(null);
  };

  // Determine if we should show expanded content
  const isExpanded = !showNav && window.innerWidth >= 1440;
  const isCollapsed = showNav || window.innerWidth < 1440;

  const isDemo = window.location.href.includes("demo-admin");

  const NewSubmissionsCount = () => {
    if (
      statsData?.total_new_submissions &&
      statsData?.total_new_submissions > 0
    )
      return (
        <div className="absolute bg-[#F87171] left-[28px] rounded-full min-w-[16px] h-[16px] top-[8px] flex items-center justify-center z-[31] shadow-sm px-[4px]">
          <span className="text-[9px] font-bold text-white leading-none">
            {statsData?.total_new_submissions}
          </span>
        </div>
      );
  };

  const getItemPermissionState = (
    title: string,
  ): { hasPermission: boolean } => {
    // Check permissions for each menu item
    switch (title) {
      case "Dashboard":
        return {
          hasPermission: has({ resource: "Dashboard", action: "view" }),
        };
      case "Bookings":
        return { hasPermission: has({ resource: "Bookings", action: "view" }) };
      case "Events Calendar":
        return {
          hasPermission: has({ resource: "Calendar Events", action: "view" }),
        };
      case "Customers":
        return {
          hasPermission: has({
            resource: "Customer Management",
            action: "view",
          }),
        };
      case "Memberships":
        return {
          hasPermission: hasAny([
            { resource: "Members", action: "view" },
            { resource: "Membership Plans", action: "view" },
          ]),
        };
      case "Transactions":
        return {
          hasPermission: has({ resource: "Transactions", action: "view" }),
        };
      case "POS":
        return { hasPermission: has({ resource: "POS", action: "view" }) };
      case "Inbox":
        return {
          hasPermission: hasAny([
            { resource: "Inbox", action: "view" },
            {
              resource: "Subscriptions",
              subResource: "event subscription",
              action: "view",
            },
            {
              resource: "Subscriptions",
              subResource: "newsletter contact form",
              action: "view",
            },
            {
              resource: "Subscriptions",
              subResource: "contact us manage",
              action: "view",
            },
          ]),
        };
      case "Analytics":
        return {
          hasPermission: has({ resource: "Analytics", action: "view" }),
        };
      case "Website CMS":
        return { hasPermission: true };
      case "Lessons & Fittings":
      case "Lessons":
        return {
          hasPermission: has({ resource: "Lesson & Coach", action: "view" }),
        };
      case "Tee Times":
      case "Course Management":
      case "Cart Fleet":
      case "Weather":
      case "Caddies":
      case "Staff Scheduler":
      case "Users & Permissions":
        // Settings page handles its own tab-level permissions
        return { hasPermission: true };
      default:
        return { hasPermission: true };
    }
  };

  const isPathActive = (path: string) => {
    const currentPath = location.pathname;

    // For golf routes, use strict prefix match to differentiate siblings (tee-times, courses, etc.)
    if (path.startsWith("/golf/")) {
      return currentPath.startsWith(path);
    }

    // Default loose matching for other routes (preserves booking/bookings behavior)
    const itemBasePath = path.split("/")[1];
    return currentPath.split("/")[1]?.includes(itemBasePath);
  };

  // Map menu item titles to their corresponding icon paths
  // These icons are from the sidebar-icons folder and match the Figma design
  const getIconPath = (title: string): string | null => {
    const iconMap: Record<string, string> = {
      // Dashboard - always first
      Dashboard: dashboardIcon,

      // Bays Section
      "Simulator Bookings": bayIcon,
      Bookings: bayIcon,
      Lessons: lessonIcon,

      // Golf Course Section
      "Tee Times": teeTimeIcon,
      "Course Management": golfCourseIcon,
      "Cart Fleet": cartIcon,
      Caddies: caddieIcon,
      "Staff Scheduler": staffIcon,
      Weather: teeTimeIcon, // Using tee time icon for weather (flag icon)
      "League Manager": leagueManagerNavIcon,

      // Management Section
      Inbox: inboxIcon,
      "Events Calendar": eventsIcon,
      Events: eventsIcon,
      Customers: customersIcon,
      Memberships: membershipsIcon,
      Transactions: transactionsIcon,
      Analytics: analyticsIcon,
      "Website CMS": websiteIcon,
      "Users & Permissions": usersPermissionsIcon,
    };
    return iconMap[title] || null;
  };

  // Render a single menu item
  const renderMenuItem = (item: AppRoute, index: number) => {
    if (item.isComing) return null;

    const permissionState = getItemPermissionState(item.title);
    const isLocked = !permissionState.hasPermission;
    // League Manager opens the dedicated side panel; treat the panel-open
    // state as "active" so the entry stays highlighted while browsing leagues.
    const isLeagueEntry = item.title === "League Manager";
    const isActive = isLeagueEntry
      ? isLeaguePanelOpen || location.pathname.startsWith("/golf/leagues")
      : isPathActive(item.path);
    const iconPath = getIconPath(item.title);

    const onItemClick = () => {
      if (isLocked) return;
      if (isLeagueEntry) {
        if (isCollapsed) setShowNav(false);
        setLeaguePanelOpen(true);
        handleNavigate(item.path);
        return;
      }
      handleNavigate(item.path);
    };

    return (
      <div
        key={index}
        className={classNames("relative shrink-0", {
          "h-[50px] w-full": true,
        })}
      >
        <div
          className={classNames(
            "flex gap-[8px] h-[50px] items-center py-[4px] rounded-[10px] transition-all duration-150 relative w-full shrink-0",
            {
              "bg-[#2C3748]": !isLocked && isActive,
              "cursor-pointer hover:bg-[#2C3748]/60": !isLocked,
              "cursor-not-allowed opacity-50": isLocked,
              "justify-center": isCollapsed,
              "pl-[8px]": !isCollapsed,
            },
          )}
          onMouseEnter={(e) => isCollapsed && handleMouseEnter(item.title, e)}
          onMouseLeave={handleMouseLeave}
          onClick={onItemClick}
        >
          <div className="overflow-clip relative shrink-0 w-[32px] h-[32px] flex items-center justify-center">
            {iconPath ? (
              <img
                src={iconPath}
                alt={item.title}
                className="w-[32px] h-[32px] max-w-[32px] max-h-[32px] block"
              />
            ) : (
              <item.image
                className="w-[32px] h-[32px] max-w-[32px] max-h-[32px] transition-all duration-150"
                style={{ color: isLocked ? "#6B7A90" : "#8F9DB3" }}
              />
            )}
          </div>
          {/* Inbox notification badge - positioned relative to the menu item row */}
          {item.title === "Inbox" && <NewSubmissionsCount />}
          {isExpanded && (
            <div
              className={classNames(
                "font-medium text-[14px] flex items-center gap-2 transition-all duration-150",
                {
                  "text-[#FCFCFC]": !isLocked && isActive,
                  "text-[#E2E8F0]": !isLocked && !isActive,
                  "text-gray-500": isLocked,
                },
              )}
            >
              <span>{item.title}</span>
              {isLocked && <LockIcon className="size-3 text-gray-400" />}
            </div>
          )}
        </div>
        {/* Hover tooltip - only show when collapsed */}
        {isCollapsed && hoveredItemTitle === item.title && tooltipPosition && (
          <div
            className="fixed p-2 bg-[#2C3748] text-white text-xs rounded-lg shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-[#3D4F66]"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translateY(-50%)",
            }}
          >
            {item.title}
            {isLocked && " (Locked)"}
          </div>
        )}
      </div>
    );
  };

  // Render settings submenu item
  const renderSettingsItem = (item: SettingsRoute, index: number) => {
    const isActive = location.pathname === item.path;

    return (
      <div
        key={index}
        onClick={() => handleNavigate(item.path)}
        className={classNames(
          "py-[6px] px-[12px] ml-[46px] mr-[8%] rounded-lg cursor-pointer transition-all duration-150",
          {
            "bg-[#2C3748] text-[#FCFCFC]": isActive,
            "text-[#E2E8F0] hover:text-[#FCFCFC] hover:bg-[#2C3748]/60":
              !isActive,
          },
        )}
      >
        <span className="text-[12px] font-medium">{item.title}</span>
      </div>
    );
  };

  // Render a section with header and items
  const renderSection = (section: SidebarSection, sectionIndex: number) => {
    const isExpanded_ = expandedSections[section.id] ?? true;
    const isSettingsExpanded = expandedSettingsMenus[section.id] ?? false;

    return (
      <div key={`${section.id}-${sectionIndex}`} className="mb-0">
        {/* Section Separator - Figma style with label + line (only show if title exists) */}
        {isExpanded && section.title && sectionIndex > 0 && (
          <div className="flex gap-[16px] items-center py-[12px] px-[0px] w-full">
            <span className="text-[13px] font-medium text-[#8F9DB2] whitespace-nowrap">
              {section.title}
            </span>
            <div className="flex-1 h-[1px] bg-[#3D4F66]" />
          </div>
        )}

        {/* Section divider for collapsed state (only show if title exists) */}
        {isCollapsed && section.title && sectionIndex > 0 && (
          <div className="h-[1px] relative w-full shrink-0 my-2">
            <div className="absolute inset-0 bg-[#3D4F66]" />
          </div>
        )}

        {/* Section Items */}
        {(isExpanded_ || isCollapsed) && (
          <div
            className={classNames("flex flex-col", {
              "gap-[2px]": isCollapsed,
              "gap-0": !isCollapsed,
            })}
          >
            {section.routes.map((item, index) => {
              const prevGroup =
                index > 0 ? section.routes[index - 1].group : undefined;
              const showSubHeader =
                isExpanded && item.group && item.group !== prevGroup;
              return (
                <div key={index}>
                  {showSubHeader && (
                    <div className="flex gap-[16px] items-center py-[12px] px-[0px] w-full">
                      <span className="text-[13px] font-medium text-[#8F9DB2] whitespace-nowrap">
                        {item.group}
                      </span>
                      <div className="flex-1 h-[1px] bg-[#3D4F66]" />
                    </div>
                  )}
                  {renderMenuItem(item, index)}
                </div>
              );
            })}

            {/* Settings Submenu */}
            {section.settingsSubmenu && isExpanded && (
              <div className="mt-1">
                {/* Settings Header */}
                <div
                  className="flex items-center justify-between h-[50px] pl-[8px] py-[4px] rounded-[10px] cursor-pointer hover:bg-[#2C3748]/60 transition-all duration-150 w-full"
                  onClick={() => toggleSettingsMenu(section.id)}
                >
                  <div className="flex items-center gap-[8px]">
                    <img
                      src={setting}
                      className="size-[32px] max-w-[32px] max-h-[32px]"
                      alt="Settings"
                    />
                    <span className="text-[14px] font-medium text-[#E2E8F0]">
                      {section.settingsSubmenu.title}
                    </span>
                  </div>
                  <DownArrow
                    className={`fill-[#8F9DB2] size-3 mr-2 transition-transform duration-200 ${
                      isSettingsExpanded ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {/* Settings Items */}
                {isSettingsExpanded && (
                  <div className="mt-1 space-y-1">
                    {section.settingsSubmenu.routes.map((item, index) =>
                      renderSettingsItem(item, index),
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav
      className={`flex flex-col h-screen mr-3 flex-shrink-0 ${
        isCollapsed ? "w-[76px]" : "w-[274px]"
      } bg-gray1 transition-all duration-500 ease-in-out relative overflow-visible`}
    >
      {/* When settings or league panel is open and sidebar is expanded, show that panel inline */}
      {(isSettingsPanelOpen || isLeaguePanelOpen) && isExpanded ? (
        <>
          {/* Panel replaces all sidebar content above the bottom bar */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scroll">
            {isLeaguePanelOpen ? (
              <LeagueSidebarPanel />
            ) : (
              <SettingsSidebarPanel />
            )}
          </div>

          {/* Bottom bar - same as normal expanded, but with settings highlighted */}
          <section className="relative pt-0">
            <div className="border-t border-[#2a374b] flex gap-[32px] h-[50px] items-center justify-center w-full">
              <Link
                target="_blank"
                to="https://help.teelogix.io/en"
                className="flex items-center justify-center hover:opacity-80 transition-opacity size-[48px]"
              >
                <img
                  src={help}
                  className="size-[32px] cursor-pointer"
                  alt="Help"
                />
              </Link>

              <div
                className={`flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer size-[48px] rounded ${isTeeAgentOpen ? "bg-[#2B3545]" : ""}`}
                onClick={toggleTeeAgent}
              >
                <AgentOrb state={agentState} size={24} />
              </div>

              <div
                className="flex items-center justify-center cursor-pointer size-[48px] bg-[#2B3545] rounded"
                onClick={() => toggleSettingsPanel()}
              >
                <img src={setting} className="size-[32px]" alt="Settings" />
              </div>

              <div
                className="flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer size-[48px]"
                onClick={handleLogout}
              >
                <img src={logoutLogo} className="size-[32px]" alt="Logout" />
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          <section
            className={
              isCollapsed
                ? "absolute flex flex-col items-start justify-end left-[10px] overflow-clip rounded-bl-[20px] rounded-br-[20px] top-[20px] w-[56px]"
                : "ml-3 relative z-10"
            }
          >
            <div
              className={classNames("pb-[1.5px] relative z-50", {
                "rounded-bl-[20px] rounded-br-[20px] overflow-clip":
                  isCollapsed,
              })}
            >
              <div
                className={classNames(
                  "flex flex-col items-center justify-center relative z-50",
                  {
                    "pt-[26px] pb-[16px] h-[75px]": !isCollapsed,
                    "h-[56px] w-full overflow-clip": isCollapsed,
                  },
                )}
              >
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setOpenReleasingPopup(true);
                  }}
                >
                  {/* Show icon when collapsed, full logo when expanded */}
                  {isCollapsed ? (
                    <img
                      src={logoIcon}
                      className="[mix-blend-mode:screen] h-[32px] w-auto transition-all duration-300"
                      alt="Logo"
                      onClick={() => setShowFullNav(!showFullNav)}
                    />
                  ) : (
                    <img
                      src={logo}
                      className="[mix-blend-mode:screen] transition-all duration-300"
                      alt="Logo"
                      onClick={() => setShowFullNav(!showFullNav)}
                    />
                  )}
                </div>
              </div>

              {/* Company info section - only show when expanded */}
              {isExpanded && (
                <div ref={venuDropDownRef} className="relative w-full">
                  <button
                    onClick={() => setShowVenuDropDown(!showVenuDropDown)}
                    className="group mt-[17px] w-full"
                  >
                    <div
                      className={`bg-[#065F46] ${
                        showVenuDropDown ? "rounded-t-[10px]" : "rounded-[10px]"
                      } flex items-center justify-between px-[12px] py-[10px] cursor-pointer z-50`}
                    >
                      <div className="flex flex-col text-start">
                        <p className="text-[13px] font-semibold text-[#FCFCFC] leading-normal">
                          {venueSettingsData?.name ||
                            useTenantStore.getState().selectedTenant?.venue_name ||
                            (isDemo
                              ? "Demo Golf & Sports Clubs"
                              : "My Venue")}
                        </p>
                        <p className="text-[12px] font-normal text-[#FCFCFC]/80 leading-[1.4]">
                          {venueSettingsData?.city || ""}
                        </p>
                      </div>
                      <DownArrow
                        className={`opacity-60 size-[24px] fill-[#fcfcfc] transition-all ${
                          showVenuDropDown ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </button>

                  <div
                    className={`w-full absolute ${
                      showVenuDropDown
                        ? "max-h-fit opacity-100"
                        : "max-h-0 opacity-0"
                    } bg-[#307356] transition-all duration-300 ease-in-out overflow-hidden rounded-b-[10px] flex flex-col gap-1.5 justify-center items-center px-3 pb-2`}
                  >
                    {import.meta.env.VITE_USE_CENTRAL_AUTH === "true" &&
                    useTenantStore.getState().accounts.length > 1
                      ? useTenantStore.getState().accounts.map((account) => {
                          const isActive =
                            account.tenant_id ===
                            useTenantStore.getState().selectedTenant?.tenant_id;
                          return (
                            <button
                              key={account.tenant_id}
                              onClick={async () => {
                                if (isActive) return;
                                const token =
                                  localStorage.getItem("accessToken");
                                if (!token) {
                                  queryClient.clear();
                                  useTenantStore.getState().clearTenant();
                                  navigate("/login");
                                  return;
                                }
                                try {
                                  const res = await selectTenant(
                                    token,
                                    account.tenant_id,
                                  );
                                  queryClient.clear();
                                  useTenantStore
                                    .getState()
                                    .selectTenant(
                                      account,
                                      res.access,
                                      res.refresh,
                                    );
                                  window.location.href = "/";
                                } catch {
                                  queryClient.clear();
                                  useTenantStore.getState().clearTenant();
                                  navigate("/login");
                                }
                              }}
                              className={`py-2.5 px-3 rounded-[6px] w-full mt-1.5 text-left text-sm text-white ${
                                isActive
                                  ? "bg-[#1DA46A] font-medium"
                                  : "hover:bg-[#1DA46A]/60"
                              }`}
                            >
                              {account.venue_name}
                            </button>
                          );
                        })
                      : (
                        <button className="py-2.5 px-3 bg-[#1DA46A] rounded-[6px] w-full mt-1.5">
                          <span className="text-white font-medium text-sm">
                            {venueSettingsData?.name ||
                              useTenantStore.getState().selectedTenant
                                ?.venue_name ||
                              (isDemo
                                ? "Demo Golf & Sports Clubs"
                                : "My Venue")}
                          </span>
                        </button>
                      )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Expand/Collapse toggle button */}
          <div
            className="absolute z-50 -right-[24px] transition-all duration-300 hidden xl:block top-[200px]"
            onClick={toggleNav}
            onMouseEnter={(e) =>
              handleMouseEnter(
                showNav ? "Expand Sidebar" : "Collapse Sidebar",
                e,
              )
            }
            onMouseLeave={handleMouseLeave}
          >
            <div className="cursor-pointer">
              <DrawerButton isCollapsed={isCollapsed} />
            </div>

            {/* Toggle button tooltip */}
            {hoveredItemTitle &&
              (hoveredItemTitle === "Expand Sidebar" ||
                hoveredItemTitle === "Collapse Sidebar") &&
              tooltipPosition && (
                <div
                  className="fixed p-2 bg-[#2C3748] text-white text-xs rounded-lg shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-[#3D4F66]"
                  style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    transform: "translateY(-50%)",
                  }}
                >
                  {hoveredItemTitle}
                </div>
              )}
          </div>

          {/* Scrollable navigation section */}
          <section
            className={classNames(
              "flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scroll transition-all duration-500",
              {
                "absolute left-[10px] top-[108px] w-[56px] px-[4px] flex flex-col gap-[2px]":
                  isCollapsed,
                "w-full px-[14px] mt-4": !isCollapsed,
              },
            )}
          >
            {/* Render all sections */}
            {sections.map((section, index) => renderSection(section, index))}

            {/* Coming Soon section */}
            {isExpanded && (
              <>
                <div
                  ref={comingDropDownRef}
                  className="relative w-full transition-all duration-300 mt-4"
                >
                  <button
                    onClick={() => setShowComingDropDown(!showComingDropDown)}
                    className="group relative py-3 z-[32] bg-gray1 w-full"
                  >
                    <div className="absolute h-[1px] bg-[#2a374b] w-full mb-2 z-[32] top-0"></div>
                    <div className="flex items-center justify-between px-[12px] cursor-pointer">
                      <div className="text-start mt-1">
                        <p className="text-[13px] ml-[10px] leading-[18.15px]">
                          <span className="text-white font-semibold">
                            Coming soon
                          </span>
                        </p>
                      </div>
                      <DownArrow
                        className={`fill-[#96b8a9] mt-1 group-hover:fill-wht transition-all delay-150 mr-[12px] ${
                          showComingDropDown ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </button>

                  <div
                    className={`transition-all ml-[10px] delay-300 duration-300 ${
                      showComingDropDown
                        ? "max-h-[300px] overflow-y-auto custom-scroll opacity-100 pt-2"
                        : "max-h-0 opacity-0 overflow-hidden"
                    } w-full`}
                  >
                    {comingSoonRoutes.map((item, index) => (
                      <div key={index}>
                        <div
                          className={classNames(
                            "flex gap-[8px] h-[50px] items-center pl-[8px] py-[4px] rounded-[10px] transition-all duration-150",
                            {
                              "bg-[#2C3748]":
                                !item.isComing && isPathActive(item.path),
                              "hover:bg-[#2C3748]/60 cursor-pointer":
                                !item.isComing,
                              "opacity-50 cursor-default": item.isComing,
                            },
                          )}
                          onClick={() =>
                            !item.isComing && handleNavigate(item.path)
                          }
                        >
                          <div className="relative">
                            <item.image className="fill-[#3EDC95] group-hover:fill-[#3EDC95] size-[32px] transition-all duration-150" />
                          </div>
                          <div className="font-medium text-[14px] text-[#E2E8F0] hover:text-white transition-all duration-500">
                            {item.title}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Fixed bottom section - Help, Tee Agent, Settings, and Logout */}
          {isCollapsed ? (
            /* Collapsed state: absolute positioning */
            <>
              <div
                className="absolute bottom-[134px] left-1/2 -translate-x-1/2 w-[48px] flex items-center justify-center py-[4px] hover:bg-[#2C3748]/60 rounded-[10px] cursor-pointer transition-all"
                onMouseEnter={(e) => handleMouseEnter("Tee Agent", e)}
                onMouseLeave={handleMouseLeave}
                onClick={toggleTeeAgent}
              >
                <div
                  className={`size-[32px] flex items-center justify-center rounded-lg ${isTeeAgentOpen ? "bg-[#2C3748]" : ""}`}
                >
                  <AgentOrb state={agentState} size={24} />
                </div>
                {hoveredItemTitle === "Tee Agent" && tooltipPosition && (
                  <div
                    className="fixed p-2 bg-[#2C3748] text-white text-xs rounded-lg shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-[#3D4F66]"
                    style={{
                      top: `${tooltipPosition.top}px`,
                      left: `${tooltipPosition.left}px`,
                      transform: "translateY(-50%)",
                    }}
                  >
                    Tee Agent
                  </div>
                )}
              </div>

              <div
                className="absolute bottom-[94px] left-1/2 -translate-x-1/2 w-[48px] flex items-center justify-center py-[4px] hover:bg-[#2C3748]/60 rounded-[10px] cursor-pointer transition-all"
                onMouseEnter={(e) => handleMouseEnter("Settings", e)}
                onMouseLeave={handleMouseLeave}
                onClick={() => {
                  // When collapsed, expand sidebar and open settings
                  setShowNav(false);
                  setSettingsPanelOpen(true);
                }}
              >
                <img
                  src={setting}
                  className="size-[32px] max-w-[32px] max-h-[32px]"
                  alt="Settings"
                />
                {hoveredItemTitle === "Settings" && tooltipPosition && (
                  <div
                    className="fixed p-2 bg-[#2C3748] text-white text-xs rounded-lg shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-[#3D4F66]"
                    style={{
                      top: `${tooltipPosition.top}px`,
                      left: `${tooltipPosition.left}px`,
                      transform: "translateY(-50%)",
                    }}
                  >
                    Settings
                  </div>
                )}
              </div>

              <div
                className="absolute bottom-[54px] left-1/2 -translate-x-1/2 w-[48px] flex items-center justify-center py-[4px] hover:bg-[#2C3748]/60 rounded-[10px] transition-all"
                onMouseEnter={(e) => handleMouseEnter("Help", e)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  target="_blank"
                  to="https://help.teelogix.io/en"
                  className="flex items-center justify-center"
                >
                  <img
                    src={help}
                    className="size-[32px] cursor-pointer max-w-[32px] max-h-[32px]"
                    alt="Help"
                  />
                </Link>
                {hoveredItemTitle === "Help" && tooltipPosition && (
                  <div
                    className="fixed p-2 bg-[#2C3748] text-white text-xs rounded-lg shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-[#3D4F66]"
                    style={{
                      top: `${tooltipPosition.top}px`,
                      left: `${tooltipPosition.left}px`,
                      transform: "translateY(-50%)",
                    }}
                  >
                    Help
                  </div>
                )}
              </div>

              <div
                className="absolute bottom-[14px] left-1/2 -translate-x-1/2 w-[48px] flex items-center justify-center py-[4px] hover:bg-[#2C3748]/60 rounded-[10px] cursor-pointer transition-all"
                onMouseEnter={(e) => handleMouseEnter("Logout", e)}
                onMouseLeave={handleMouseLeave}
                onClick={handleLogout}
              >
                <img
                  src={logoutLogo}
                  className="size-[32px] max-w-[32px] max-h-[32px]"
                  alt="Logout"
                />
                {hoveredItemTitle === "Logout" && tooltipPosition && (
                  <div
                    className="fixed p-2 bg-[#2C3748] text-white text-xs rounded-lg shadow-xl drop-shadow-lg z-[9999] whitespace-nowrap pointer-events-none border border-[#3D4F66]"
                    style={{
                      top: `${tooltipPosition.top}px`,
                      left: `${tooltipPosition.left}px`,
                      transform: "translateY(-50%)",
                    }}
                  >
                    Logout
                    {isPending && <span className="ml-2">...</span>}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Expanded state: flex row */
            <section className="relative pt-0">
              <div className="border-t border-[#2a374b] flex gap-[32px] h-[50px] items-center justify-center w-full">
                <Link
                  target="_blank"
                  to="https://help.teelogix.io/en"
                  className="flex items-center justify-center hover:opacity-80 transition-opacity size-[48px]"
                >
                  <img
                    src={help}
                    className="size-[32px] cursor-pointer"
                    alt="Help"
                  />
                </Link>

                <div
                  className={`flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer size-[48px] rounded ${isTeeAgentOpen ? "bg-[#2B3545]" : ""}`}
                  onClick={toggleTeeAgent}
                >
                  <AgentOrb state={agentState} size={24} />
                </div>

                <div
                  className="flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer size-[48px]"
                  onClick={() => toggleSettingsPanel()}
                >
                  <img src={setting} className="size-[32px]" alt="Settings" />
                </div>

                <div
                  className="flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer size-[48px]"
                  onClick={handleLogout}
                >
                  <img src={logoutLogo} className="size-[32px]" alt="Logout" />
                </div>
              </div>
            </section>
          )}

          {/* Settings Slide-Out Panel - removed, now inline */}
        </>
      )}
    </nav>
  );
};

export default AppSidebar;
